import React, { useState } from 'react'
import {
  Accordion,
  AccordionSummary,
  FormControlLabel,
  Radio,
  Typography,
  AccordionDetails,
  makeStyles,
  RadioGroup,
} from '@material-ui/core'
import Image from 'next/image'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import { useTranslation } from 'react-i18next'
import { PAYMENT_ACCORDION } from '@/utils/constants'
import clsx from 'clsx'

const useStyles = makeStyles(() => ({
  root: {
    margin: '15px 0',
  },
  formControlLabel: {
    width: '100%',
  },
  accordionSummaryLabel: {
    width: '100%',
  },
  accordionTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    width: '100%',
  },
  accordionContent: {
    paddingLeft: 12,
    paddingRight: 0,
    '&>.MuiAccordionSummary-content': {
      justifyContent: 'space-between',
      alignItems: 'center',
      '&.Mui-expanded': {
        margin: 0,
      },
    },
    '& .MuiFormControlLabel-root': {
      display: 'flex',
      width: '100%',
    },
    '& .MuiFormControlLabel-label': {
      flexGrow: 1,
      '& .MuiTypography-root': {
        lineHeight: 1,
      },
    },
  },
  tagLineWithIcon: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  icon: {
    width: '20px!important',
  },
  cardTagline: {
    display: 'flex',
    alignItems: 'center',
  },
  cardIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    '& span': {
      width: '20px!important',
    },
  },
}))

type Tprops = {
  cardComponent: React.ReactNode
  paypalComponent: React.ReactNode
}

const PaymentAccordion = ({ cardComponent, paypalComponent }: Tprops) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [selectedPayment, setSelectedPayment] = useState('')

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPayment(event.target.value)
  }

  return (
    <RadioGroup
      value={selectedPayment}
      onChange={handleChange}
      className={classes.root}
    >
      <Accordion expanded={selectedPayment === PAYMENT_ACCORDION.CARD}>
        <AccordionSummary
          aria-controls="card-content"
          id="card-header"
          className={classes.accordionContent}
        >
          <FormControlLabel
            value="card"
            control={
              <Radio
                color="primary"
                className={'carrier-accordion__radio'}
                aria-label="Credit card"
              />
            }
            className={classes.formControlLabel}
            classes={{ label: classes.accordionSummaryLabel }}
            label={
              <div className={classes.accordionTitle}>
                <div className={classes.title}>
                  <Typography>
                    {t('texts:funnel:payment:payment_accordion:card_title')}
                  </Typography>
                  <div className={classes.tagLineWithIcon}>
                    <Typography variant="body2" className={classes.cardTagline}>
                      {t('texts:funnel:payment:payment_accordion:card_tagline')}
                    </Typography>
                    <div className={classes.cardIcons}>
                      <Image
                        src={'/icon-mastercard.svg'}
                        alt={'mastercard'}
                        width={20}
                        height={20}
                        className={clsx(
                          'payment-accordion__card-icon',
                          classes.icon
                        )}
                      />
                      <CreditCardIcon
                        className={'payment-accordion__card-icon'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            }
            onClick={(event) => event.stopPropagation()}
            onFocus={(event) => event.stopPropagation()}
          />
        </AccordionSummary>
        <AccordionDetails>
          <Typography>{cardComponent}</Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={selectedPayment === PAYMENT_ACCORDION.PAYPAL}>
        <AccordionSummary
          aria-controls="paypal-content"
          id="paypal-header"
          className={classes.accordionContent}
        >
          <FormControlLabel
            value="paypal"
            control={
              <Radio
                color="primary"
                className={'carrier-accordion__radio'}
                aria-label="Paypal"
              />
            }
            className={classes.formControlLabel}
            classes={{ label: classes.accordionSummaryLabel }}
            label={
              <div className={classes.accordionTitle}>
                <Typography>
                  {t('texts:funnel:payment:payment_accordion:paypal_title')}
                </Typography>
                <Image
                  src={'/icon-paypal.svg'}
                  alt={'paypal'}
                  width={28}
                  height={20}
                  className={'payment-accordion__card-icon'}
                />
              </div>
            }
            onClick={(event) => event.stopPropagation()}
            onFocus={(event) => event.stopPropagation()}
          />
        </AccordionSummary>
        <AccordionDetails>
          <Typography>{paypalComponent}</Typography>
        </AccordionDetails>
      </Accordion>
    </RadioGroup>
  )
}

export default PaymentAccordion
